/**
 * The conversational UI shared by the chat home (`/`) and `/conversations/$id`.
 *
 * Layout: a conversation sidebar (desktop + mobile sheet) beside a message pane
 * with an auto-scrolling stream and a fixed composer. Messages stream live from
 * Firestore (see `useMessages`); the assistant reply is a `status:'streaming'`
 * document the backend mutates in place.
 *
 * Sending: calls the `sendMessage` callable. When there is no active
 * conversation, the callable creates one and returns its id — we then navigate
 * to `/conversations/$id` so the URL reflects the live thread.
 *
 * Optimistic UI: the user's message is shown immediately (before the round
 * trip) and a "thinking" placeholder fills in for the assistant reply until
 * either the real streaming doc appears or the turn errors — see
 * `lib/optimisticMessages.ts` (the module-level store, keyed per conversation,
 * survives the `/` → `/conversations/$id` remount) and `useOptimisticTurns`.
 */
import type { Attachment, ReminderDoc } from '@agente/shared'
import { normalizeDisabledTools } from '@agente/shared'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { MessageCircle, PanelLeft, Plug, Sparkles, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Monogram } from '@/components/brand/logo'
import { ChatComposerError } from '@/components/chat/chat-composer-error'
import { Composer } from '@/components/chat/composer'
import { ConversationList } from '@/components/chat/conversation-list'
import { MessageBubble } from '@/components/chat/message-bubble'
import { ModelPicker } from '@/components/chat/model-picker'
import { PendingUserBubble } from '@/components/chat/pending-user-bubble'
import { ThinkingBubble } from '@/components/chat/thinking-bubble'
import { ToolsPanel } from '@/components/chat/tools-panel'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { VoiceOverlay } from '@/components/voice/voice-overlay'
import { useConversations } from '@/hooks/useConversations'
import { useMessages } from '@/hooks/useMessages'
import { useOptimisticTurns } from '@/hooks/useOptimisticTurns'
import { useReminders } from '@/hooks/useReminders'
import { useUserDoc } from '@/hooks/useUserDoc'
import { sendMessage } from '@/lib/callables'
import { formatDateTime } from '@/lib/format'
import {
  addPendingTurn,
  getPendingTurns,
  movePendingTurns,
  NEW_CONVERSATION_KEY,
  removePendingTurn,
  retryPendingTurn,
  setPendingTurnError,
} from '@/lib/optimisticMessages'

/** Feature flag da conversa por voz (baked pelo Vite em build time). */
const VOICE_ENABLED = import.meta.env.VITE_VOICE_ENABLED === 'true'

const SUGGESTIONS = [
  'O que tenho na agenda amanhã?',
  'Prepare um checklist para minha próxima reunião.',
  'Quem é o cliente da reunião de hoje à tarde?',
  'Crie um lembrete para revisar o contrato às 17h.',
]

/** Primeira mensagem enviada quando o usuário aceita a apresentação inicial. */
const ONBOARDING_OPENER = 'Olá, vamos começar.'

/** Monta o rascunho pt-BR para conversar sobre um lembrete encaminhado ao chat. */
function buildReminderDraft(reminder: ReminderDoc): string {
  let draft = `Quero conversar sobre o lembrete "${reminder.title}" (prazo ${formatDateTime(reminder.dueAt)}).`
  // O summary só é acrescentado para lembretes de e-mail (prévia da mensagem, útil).
  // Em lembretes de chat o summary é a própria mensagem original do usuário → redundante.
  if (reminder.sourceContext?.origin === 'email') {
    const summary = reminder.sourceContext.summary?.trim()
    if (summary) draft += ` Contexto: ${summary}`
  }
  return draft
}

export function ChatView({ conversationId }: { conversationId: string | null }) {
  const navigate = useNavigate()
  const conversationsResult = useConversations()
  const { messages, loading: messagesLoading } = useMessages(conversationId)
  // Chave do turno otimista: a conversa real já tem id, ou o sentinela de
  // "conversa nova" enquanto a primeira mensagem está em voo a partir de `/`
  // (ver lib/optimisticMessages.ts — o estado é module-level de propósito,
  // pois esta rota e /conversations/$id são componentes distintos e a
  // navegação após a criação da conversa desmonta este ChatView).
  const conversationKey = conversationId ?? NEW_CONVERSATION_KEY
  const { pendingUserTurns, showThinking } = useOptimisticTurns(conversationKey, messages)
  const { userDoc, loading: userLoading } = useUserDoc()
  // Onboarding pendente enquanto a flag `completedAt` estiver ausente. Só decidido
  // após o user doc carregar, para não piscar o CTA no primeiro render.
  const onboardingPending = !userLoading && !userDoc?.onboarding?.completedAt

  // Aviso de integração Microsoft: exibido só depois que o user doc carrega e
  // quando a conta não está conectada. Dispensável apenas nesta sessão (estado
  // local — não persistimos a dispensa no Firestore).
  const [microsoftBannerDismissed, setMicrosoftBannerDismissed] = useState(false)
  // Reconexão necessária: token válido porém defasado (escopo novo). O doc mantém
  // `connected: true` nesse caso, então a condição do banner precisa cobrir tanto
  // a primeira conexão (!connected) quanto o nudge de reconexão explicitamente.
  // Lido do flag sticky `reconnectRequired` (não do `status`, que o getAccessToken
  // sobrescreve a cada refresh de token).
  const microsoftNeedsReconnect = userDoc?.microsoft?.reconnectRequired === true
  const showMicrosoftBanner =
    !userLoading &&
    (userDoc?.microsoft?.connected !== true || microsoftNeedsReconnect) &&
    !microsoftBannerDismissed

  // Encaminhar lembrete ao chat: a rota '/' aceita ?reminderId. Ao chegar com um,
  // montamos um rascunho pré-preenchido (sem enviar) e limpamos o search param
  // para não re-disparar em re-renders. Consumido uma única vez por id.
  const { reminderId } = useSearch({ strict: false }) as { reminderId?: string }
  const { reminders, loading: remindersLoading } = useReminders()
  const [reminderDraft, setReminderDraft] = useState('')
  const consumedReminderRef = useRef<string | null>(null)

  const [mobileListOpen, setMobileListOpen] = useState(false)
  const [voiceOpen, setVoiceOpen] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!reminderId || remindersLoading) return
    if (consumedReminderRef.current === reminderId) return
    consumedReminderRef.current = reminderId
    const reminder = reminders.find((r) => r.id === reminderId)
    if (reminder) setReminderDraft(buildReminderDraft(reminder))
    // Limpa o search param (mesmo se o lembrete não existir mais) para não repetir.
    void navigate({ to: '/', search: {}, replace: true })
  }, [reminderId, remindersLoading, reminders, navigate])
  // Id da conversa criada AO VIVO por uma sessão de voz iniciada em `/`. A
  // navegação para /conversations/$id é adiada até o overlay fechar (ver
  // onOpenChange do VoiceOverlay): navegar durante a sessão remontaria a rota
  // (ChatRoute e ConversationRoute são componentes distintos) e desmontaria o
  // overlay, matando a LiveSession no meio da conversa. (Alternativa futura:
  // rota compartilhada ou provider global de voz evitaria o remount.)
  const createdConversationIdRef = useRef<string | null>(null)

  const activeConversation = useMemo(
    () =>
      conversationId
        ? (conversationsResult.conversations.find((c) => c.id === conversationId) ?? null)
        : null,
    [conversationId, conversationsResult.conversations],
  )
  const activeTitle = conversationId ? (activeConversation?.title ?? 'Conversa') : 'Nova conversa'
  // Normalizado no ponto de leitura: o campo vem do doc da conversa (escrita
  // direta do cliente) e um shape inválido degrada para [] sem quebrar a UI.
  const activeDisabledTools = useMemo(
    () => normalizeDisabledTools(activeConversation?.disabledTools),
    [activeConversation?.disabledTools],
  )

  // Auto-scroll to the newest content as messages arrive / stream. The effect
  // body reads only the ref, but we intentionally re-run it on the last
  // message's id/content/status so the view follows live streaming updates.
  // On a conversation switch (ChatView stays mounted — no remount) we jump
  // instantly so the pane doesn't animate-scroll from the previous position;
  // within a conversation we scroll smoothly to follow the live stream.
  const lastMessage = messages[messages.length - 1]
  const prevConversationId = useRef(conversationId)
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps intentionally drive re-scroll on streaming/optimistic updates
  useEffect(() => {
    const switched = prevConversationId.current !== conversationId
    prevConversationId.current = conversationId
    bottomRef.current?.scrollIntoView({
      behavior: switched ? 'auto' : 'smooth',
      block: 'end',
    })
  }, [
    conversationId,
    lastMessage?.id,
    lastMessage?.content,
    lastMessage?.status,
    pendingUserTurns.length,
    showThinking,
  ])

  /** Envia (ou reenvia) um turno já registrado no store otimista (ver `handleSend`/`handleRetryTurn`). */
  async function sendTurn(localId: string, text: string, attachments: Attachment[]) {
    try {
      const { data } = await sendMessage({
        conversationId: conversationId ?? undefined,
        text,
        attachments: attachments.length > 0 ? attachments : undefined,
      })
      // A brand-new conversation: move the pending turn to the real id (so the
      // ChatView instance that mounts at /conversations/$id finds it in the
      // module-level store) before navigating there.
      if (!conversationId && data.conversationId) {
        movePendingTurns(NEW_CONVERSATION_KEY, data.conversationId)
        await navigate({ to: '/conversations/$id', params: { id: data.conversationId } })
      }
    } catch (err) {
      // Fica visível na bolha otimista (com "Tentar de novo"/"Descartar") em vez
      // de restaurar o rascunho no composer ou sumir silenciosamente.
      const message =
        err instanceof Error ? err.message : 'Não foi possível enviar. Tente novamente.'
      setPendingTurnError(conversationKey, localId, message)
    }
  }

  async function handleSend(text: string, attachments: Attachment[]) {
    const localId = addPendingTurn(conversationKey, { text, attachments })
    await sendTurn(localId, text, attachments)
  }

  function handleRetryTurn(localId: string) {
    const turn = getPendingTurns(conversationKey).find((t) => t.localId === localId)
    if (!turn) return
    retryPendingTurn(conversationKey, localId)
    void sendTurn(localId, turn.text, turn.attachments)
  }

  function handleDiscardTurn(localId: string) {
    removePendingTurn(conversationKey, localId)
  }

  const hasOptimisticContent = pendingUserTurns.length > 0 || showThinking
  const showEmptyState = conversationId
    ? !messagesLoading && messages.length === 0 && !hasOptimisticContent
    : !hasOptimisticContent
  // Trava o composer só enquanto ESTA conversa tem um turno em voo — não inclui
  // turnos com erro (o usuário precisa poder digitar uma nova mensagem mesmo
  // com uma bolha de erro pendente de retry/descarte ao lado).
  const composerBusy = showThinking

  return (
    <div className="flex h-full min-h-0">
      {/* Conversation sidebar (desktop) */}
      <aside className="border-border bg-sidebar hidden w-64 shrink-0 flex-col border-r p-2 lg:flex">
        <ConversationList {...conversationsResult} activeId={conversationId} />
      </aside>

      {/* Conversation sidebar (mobile sheet) */}
      {mobileListOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="bg-black/50 absolute inset-0"
            aria-label="Fechar lista de conversas"
            onClick={() => setMobileListOpen(false)}
          />
          <aside className="border-border bg-sidebar absolute inset-y-0 left-0 flex w-72 flex-col border-r p-2">
            <ConversationList
              {...conversationsResult}
              activeId={conversationId}
              onNavigate={() => setMobileListOpen(false)}
            />
          </aside>
        </div>
      ) : null}

      {/* Message pane */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-border flex h-11 shrink-0 items-center gap-2 border-b px-3">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label="Conversas"
            onClick={() => setMobileListOpen(true)}
          >
            <PanelLeft className="size-4" />
          </Button>
          <h1 className="min-w-0 truncate font-display text-sm font-semibold tracking-tight">
            {activeTitle}
          </h1>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <ModelPicker />
            <ToolsPanel conversationId={conversationId} disabledTools={activeDisabledTools} />
          </div>
        </div>

        {activeConversation?.channel === 'whatsapp' ? (
          <div className="border-border bg-muted/40 flex shrink-0 items-start gap-3 border-b px-4 py-3">
            <MessageCircle className="text-brand mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <p className="min-w-0 flex-1 text-sm">
              Esta conversa é compartilhada com o seu WhatsApp. Mensagens enviadas pelo WhatsApp são
              respondidas lá e aparecem aqui; o que você escrever neste app é respondido aqui.
            </p>
          </div>
        ) : null}

        {showMicrosoftBanner ? (
          <div className="border-border bg-muted/40 flex shrink-0 items-start gap-3 border-b px-4 py-3">
            <Plug className="text-brand mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm">
                {microsoftNeedsReconnect
                  ? 'Novos recursos foram adicionados. Reconecte sua conta Microsoft para habilitá-los.'
                  : 'Conecte sua conta Microsoft para que o Agente Executivo possa consultar sua agenda e seus e-mails e enviar mensagens no Teams.'}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void navigate({ to: '/settings' })}
              >
                {microsoftNeedsReconnect ? 'Reconectar Microsoft' : 'Conectar Microsoft'}
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-mr-1 size-7 shrink-0"
              aria-label="Dispensar aviso"
              onClick={() => setMicrosoftBannerDismissed(true)}
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : null}

        <ScrollArea className="min-h-0 flex-1">
          <div
            // Re-keying on the conversation replays the entrance animation, so
            // switching threads crossfades the pane instead of snapping.
            key={conversationId ?? 'new'}
            className="animate-in fade-in-0 slide-in-from-bottom-2 mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6 duration-300 ease-out"
          >
            {conversationId && messagesLoading && !hasOptimisticContent ? (
              <div className="space-y-4">
                <Skeleton className="ml-auto h-12 w-2/3 rounded-2xl" />
                <Skeleton className="h-20 w-3/4 rounded-2xl" />
                <Skeleton className="ml-auto h-10 w-1/2 rounded-2xl" />
              </div>
            ) : showEmptyState ? (
              <EmptyState
                onPick={(s) => void handleSend(s, [])}
                disabled={composerBusy}
                loading={userLoading}
                onboardingPending={onboardingPending}
              />
            ) : (
              <>
                {messages.map((m) => (
                  <div key={m.id} className="animate-in fade-in-0 duration-300 ease-out">
                    <MessageBubble message={m} conversationId={conversationId} />
                  </div>
                ))}
                {/* Turnos otimistas: enviados agora mesmo, ainda sem eco confirmado
                    no Firestore (ver useOptimisticTurns). Sobrevivem à navegação
                    para /conversations/$id numa conversa nova (store module-level). */}
                {pendingUserTurns.map((turn) => (
                  <div key={turn.localId} className="animate-in fade-in-0 duration-300 ease-out">
                    <PendingUserBubble
                      turn={turn}
                      onRetry={handleRetryTurn}
                      onDiscard={handleDiscardTurn}
                    />
                  </div>
                ))}
                {showThinking ? (
                  <div className="animate-in fade-in-0 duration-300 ease-out">
                    <ThinkingBubble />
                  </div>
                ) : null}
              </>
            )}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        <div className="border-border border-t p-3">
          <div className="mx-auto w-full max-w-3xl">
            <ChatComposerError error={conversationsResult.error} />
            <Composer
              onSend={handleSend}
              sending={composerBusy}
              onVoiceClick={() => setVoiceOpen(true)}
              initialDraft={reminderDraft}
            />
            <p className="text-muted-foreground mt-1.5 px-1 text-center text-[0.6875rem]">
              O agente pode cometer erros. Confira dados sensíveis nas fontes oficiais.
            </p>
          </div>
        </div>
      </div>

      {VOICE_ENABLED ? (
        <VoiceOverlay
          open={voiceOpen}
          onOpenChange={(next) => {
            setVoiceOpen(next)
            // Navegação adiada: só ao FECHAR o overlay e só se a sessão criou uma
            // conversa nova estando em `/`. O overlay só chama onOpenChange(false)
            // depois que stop() concluiu, então a navegação (que remonta a rota)
            // não corta mais a sessão. Voz iniciada de dentro de /conversations/$id
            // não cria conversa nova (id já existe) e não navega.
            if (!next && conversationId === null) {
              const createdId = createdConversationIdRef.current
              if (createdId) {
                createdConversationIdRef.current = null
                void navigate({ to: '/conversations/$id', params: { id: createdId } })
              }
            }
          }}
          conversationId={conversationId}
          onConversationCreated={(id) => {
            // Guarda o id (persistência à prova de crash já aconteceu no hook);
            // a navegação é adiada para o fechamento do overlay (ver onOpenChange).
            createdConversationIdRef.current = id
          }}
        />
      ) : null}
    </div>
  )
}

function EmptyState({
  onPick,
  disabled,
  loading,
  onboardingPending,
}: {
  onPick: (suggestion: string) => void
  disabled?: boolean
  loading?: boolean
  onboardingPending?: boolean
}) {
  // Enquanto o user doc carrega não sabemos se é primeiro acesso ou retorno.
  // Mostramos um placeholder neutro para não piscar a copy de retorno para quem
  // nunca acessou (nem o CTA de onboarding para quem já concluiu).
  if (loading) {
    return (
      <div className="flex flex-col items-center gap-6 py-10 text-center">
        <Skeleton className="size-14 rounded-2xl" />
        <div className="flex flex-col items-center gap-2">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-72" />
        </div>
      </div>
    )
  }

  // Primeiro acesso: o secretário quer se apresentar e aprender preferências. O
  // clique envia a abertura via `onPick` (mesmo mecanismo de envio); nada é
  // enviado automaticamente no render — o usuário permanece no controle.
  if (onboardingPending) {
    return (
      <div className="flex flex-col items-center gap-6 py-10 text-center">
        <div className="bg-card border-border flex size-14 items-center justify-center rounded-2xl border shadow-sm">
          <Monogram size={30} />
        </div>
        <div className="space-y-1">
          <h2 className="font-display text-xl font-semibold tracking-tight">Vamos nos conhecer?</h2>
          <p className="text-muted-foreground mx-auto max-w-md text-sm">
            Antes de começar, o Agente Executivo gostaria de se apresentar e aprender como você
            prefere trabalhar. Leva só um minuto.
          </p>
        </div>
        <Button
          type="button"
          disabled={disabled}
          onClick={() => onPick(ONBOARDING_OPENER)}
          className="shadow-sm"
        >
          <Sparkles className="size-4" />
          Começar apresentação
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-6 py-10 text-center">
      <div className="bg-card border-border flex size-14 items-center justify-center rounded-2xl border shadow-sm">
        <Monogram size={30} />
      </div>
      <div className="space-y-1">
        <h2 className="font-display text-xl font-semibold tracking-tight">
          Como podemos ajudar você hoje?
        </h2>
        <p className="text-muted-foreground mx-auto max-w-md text-sm">
          Peça um resumo da agenda, prepare reuniões e viagens, consulte clientes ou crie lembretes
          — o Agente Executivo cuida do resto.
        </p>
      </div>
      <div className="grid w-full max-w-lg gap-2 sm:grid-cols-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            disabled={disabled}
            onClick={() => onPick(s)}
            className="border-border bg-card hover:bg-accent hover:text-accent-foreground flex items-start gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors disabled:opacity-50"
          >
            <Sparkles className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            <span>{s}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
